import React, { useContext } from 'react';
import FeatureGatesContext from '../contexts/FeatureGatesContext';
import CurrentUserContext from '../contexts/CurrentUserContext';
import Tooltip from './Tooltip';
import Icon from '../kingpin/atoms/Icon';

const GOALS: FleetOps.FeatureGate = 'goals';
const SELF_SERVE: FleetOps.FeatureGate = 'selfServe';
const MICROSOFT_SSO: FleetOps.FeatureGate = 'microsoftSso';
const ENTITY_DETAILS: FleetOps.FeatureGate = 'entityDetails';
const ACTIVITY_COSTS: FleetOps.FeatureGate = 'activityCosts';
const FILE_UPLOAD: FleetOps.FeatureGate = 'fileUpload';
const CUSTOMER_AWARDS_TRACKER: FleetOps.FeatureGate = 'customerAwardsTracker';
const DRIVER_BONUS: FleetOps.FeatureGate = 'driverBonus';
const PERFORMANCE: FleetOps.FeatureGate = 'performance';
const TEMPLATES: FleetOps.FeatureGate = 'templates';
const VIEWER_ROLE_ASSIGNMENT: FleetOps.FeatureGate = 'viewerRoleAssignment';
const PORTALS_SE_PREVIEW: FleetOps.FeatureGate = 'portalsSEPreview';
const PORTALS_PER_USER_UI_TOGGLE: FleetOps.FeatureGate =
  'portalUIPerUserToggle';
const PORTALS_ACCOUNT_WIDE_TOGGLE: FleetOps.FeatureGate =
  'portalAccountWideToggle';
const V3_PUBLIC_CONTENT: FleetOps.FeatureGate = 'v3PublicContent';
const DRIVER_SETUP_AND_APP_ACCESS: FleetOps.FeatureGate =
  'driverSetupAndAppAccess';
const DRIVER_BONUS_EXCLUSION: FleetOps.FeatureGate = 'driverBonusExclusion';
const FISCAL_PERIODS: FleetOps.FeatureGate = 'fiscalPeriods';
const DRIVER_SMS_INVITE: FleetOps.FeatureGate = 'driverSmsInvite';
const ADDITIONAL_PAYMENTS: FleetOps.FeatureGate = 'additionalPayments';
const SCHEDULED_REPORTS: FleetOps.FeatureGate = 'scheduledReports';
const AI_INSIGHTS_CHAT: FleetOps.FeatureGate = 'aiInsightsChat';
const DRIVER_APP_EMBED_PREVIEW: FleetOps.FeatureGate = 'driverAppEmbedPreview';
const DRIVER_APP_AUTO_INVITE: FleetOps.FeatureGate = 'driverAppAutoInvite';
const STUDIO: FleetOps.FeatureGate = 'studio';
const DATA_TABLE: FleetOps.FeatureGate = 'dataTable';

export const FEATURE_GATES = {
  GOALS,
  SELF_SERVE,
  MICROSOFT_SSO,
  ENTITY_DETAILS,
  ACTIVITY_COSTS,
  FILE_UPLOAD,
  CUSTOMER_AWARDS_TRACKER,
  DRIVER_BONUS,
  PERFORMANCE,
  TEMPLATES,
  VIEWER_ROLE_ASSIGNMENT,
  PORTALS_SE_PREVIEW,
  PORTALS_PER_USER_UI_TOGGLE,
  PORTALS_ACCOUNT_WIDE_TOGGLE,
  V3_PUBLIC_CONTENT,
  DRIVER_SETUP_AND_APP_ACCESS,
  DRIVER_BONUS_EXCLUSION,
  FISCAL_PERIODS,
  DRIVER_SMS_INVITE,
  ADDITIONAL_PAYMENTS,
  SCHEDULED_REPORTS,
  AI_INSIGHTS_CHAT,
  DRIVER_APP_EMBED_PREVIEW,
  DRIVER_APP_AUTO_INVITE,
  STUDIO,
  DATA_TABLE,
};

const FeatureGate = ({
  featureName,
  children,
  fallback,
}: {
  featureName: FleetOps.FeatureGate;
  children: JSX.Element | JSX.Element[];
  fallback?: JSX.Element | JSX.Element[];
}) => {
  const { isFleetOpsStaff } = useContext(CurrentUserContext);
  const {
    isGoalsEnabled,
    isSelfServeEnabled,
    isMicrosoftSsoEnabled,
    isEntityDetailsEnabled,
    isActivityCostsEnabled,
    isFileUploadEnabled,
    isCustomerAwardsTrackerEnabled,
    isDriverBonusEnabled,
    isPerformanceEnabled,
    isTemplatesEnabled,
    isViewerRoleAssignmentEnabled,
    isPortalsSEPreviewEnabled,
    isPortalsUIPerUserToggleEnabled,
    isPortalsAccountWideToggleEnabled,
    isV3PublicContentEnabled,
    isDriverSetupAndAppAccessEnabled,
    isDriverBonusExclusionEnabled,
    isFiscalPeriodsEnabled,
    isDriverSmsInviteEnabled,
    isAdditionalPaymentsEnabled,
    isScheduledReportsEnabled,
    isAiInsightsChatEnabled,
    isDriverAppEmbedPreviewEnabled,
    isDriverAppAutoInviteEnabled,
    isStudioEnabled,
    isDataTableEnabled,
  } = useContext(FeatureGatesContext);

  if (featureName === FEATURE_GATES.GOALS && isGoalsEnabled) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  if (featureName === FEATURE_GATES.SELF_SERVE && isSelfServeEnabled) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.MICROSOFT_SSO && isMicrosoftSsoEnabled) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.ENTITY_DETAILS && isEntityDetailsEnabled) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.ACTIVITY_COSTS && isActivityCostsEnabled) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.PORTALS_SE_PREVIEW &&
    isPortalsSEPreviewEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.PORTALS_ACCOUNT_WIDE_TOGGLE &&
    isPortalsAccountWideToggleEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.PORTALS_PER_USER_UI_TOGGLE &&
    isPortalsUIPerUserToggleEnabled
  ) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.FILE_UPLOAD) {
    if (!isFileUploadEnabled && isFleetOpsStaff) {
      return (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Tooltip
              isFlexAnchor
              content="Please note that you can only see this content as you are FleetOps
              Staff. To make this available to the customer, enable the 'File Upload' feature gate in the advanced area."
            >
              <Icon icon={'warning'} color={'var(--color-status-warning)'} />
            </Tooltip>
          </div>
          {children}
        </>
      );
    }
    if (isFileUploadEnabled) {
      return <>{children}</>;
    }
    return null;
  }

  if (
    featureName === FEATURE_GATES.CUSTOMER_AWARDS_TRACKER &&
    isCustomerAwardsTrackerEnabled
  ) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.DRIVER_BONUS && isDriverBonusEnabled) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.PERFORMANCE && isPerformanceEnabled) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.TEMPLATES && isTemplatesEnabled) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.VIEWER_ROLE_ASSIGNMENT &&
    isViewerRoleAssignmentEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.V3_PUBLIC_CONTENT &&
    isV3PublicContentEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.DRIVER_SETUP_AND_APP_ACCESS &&
    isDriverSetupAndAppAccessEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.DRIVER_BONUS_EXCLUSION &&
    isDriverBonusExclusionEnabled
  ) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.FISCAL_PERIODS && isFiscalPeriodsEnabled) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.DRIVER_SMS_INVITE &&
    isDriverSmsInviteEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.ADDITIONAL_PAYMENTS &&
    isAdditionalPaymentsEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.SCHEDULED_REPORTS &&
    isScheduledReportsEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.AI_INSIGHTS_CHAT &&
    isAiInsightsChatEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.DRIVER_APP_EMBED_PREVIEW &&
    isDriverAppEmbedPreviewEnabled
  ) {
    return <>{children}</>;
  }

  if (
    featureName === FEATURE_GATES.DRIVER_APP_AUTO_INVITE &&
    isDriverAppAutoInviteEnabled
  ) {
    return <>{children}</>;
  }

  if (featureName === FEATURE_GATES.STUDIO && isStudioEnabled) {
    return <>{children}</>;
  }

  // The account's DataTable setting only. To choose a table renderer, use
  // DataTable's useIsDataTable, which applies the surface's rollout stage.
  if (featureName === FEATURE_GATES.DATA_TABLE && isDataTableEnabled) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return null;
};

export default FeatureGate;
